/**
 * A shared dashboard, signed out (`/d/<token>`; screenshots/115, brief §2.20): no sidebar, a public bar (view only ·
 * snapshot time · shared by, Sign in, Get a Civic Pass), read-only, the dashboard's filters as shared, and the same
 * widgets with team-only ones locked at the same size (redactForLink, done by the Worker). A missing or expired link
 * is the "This link expired." system page (screenshots/106).
 */
import { useEffect } from "react";
import { Eye, LogIn, Printer, Ticket } from "lucide-react";
import { SystemPage } from "../system/SystemPage";
import { Grid } from "../overview/OverviewGrid";
import { CrossFilterBar } from "../overview/OverviewPage";
import { ChartEditorHost } from "../overview/ChartEditor";
import { MobileGrid, usePhone } from "./DashboardPage";
import { MobileFilterChips } from "./DashboardFilters";
import { filterChip } from "./pageFilters";
import { useState } from "react";
import { PrintView } from "./PrintView";
import { widgetViews } from "../overview/widgetViews";
import { useOverview } from "../overview/overviewStore";
import type { PublicDashboardSnapshot } from "./types";
import type { DatasetResult } from "../overview/types";

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" }) : "");

/** Live links re-query through the link (no session): /api/datasets/… → /api/public/<token>/datasets/…. */
export function routeDatasetsThroughLink(token: string) {
  const orig = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const m = /^(?:https?:\/\/[^/]+)?\/api\/datasets\/(.*)$/.exec(url);
    return orig(m ? `/api/public/${encodeURIComponent(token)}/datasets/${m[1]}` : input, init);
  }) as typeof window.fetch;
}

export function PublicDashboard({ snap }: { snap: PublicDashboardSnapshot | { error: number } }) {
  const phone = usePhone();
  const [printing, setPrinting] = useState<Map<string, { ds: DatasetResult; title: string }> | null>(null);
  const filters = useOverview((s) => s.pageFilters);
  useEffect(() => { document.title = "error" in snap ? "Chicago Politics" : `${snap.dashboard.title} · Chicago Politics`; }, [snap]);
  if ("error" in snap) {
    return <SystemPage kind="expired" />;
  }
  const d = snap.dashboard;
  // The weekly PDF (dashboard-analytics-pdf.js): Browser Rendering prints this page, so it is only the print pages.
  if (snap.print) return <PrintView dashboard={{ id: "pdf", title: d.title, layout: d.layout, print: d.print, link: d.link, filters: d.filters, version: d.version, updatedAt: d.updatedAt }}
    orgName={d.orgName} filters={filters} views={new Map()} link={d.link.token ? `${location.host}/d/${d.link.token}` : undefined} onClose={() => {}} onSaveSetup={() => {}} />;
  const stamp = d.link.data === "live" ? "Live" : `Snapshot · ${new Date(d.link.publishedAt ?? snap.at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "America/Chicago" })}`;
  const linkNext = encodeURIComponent(`/d/${d.link.token}`);
  return (
    <div className="app app--bare an-public">
      {/* Public bar (screenshots/115): view only · snapshot time · shared by, Sign in, Get a Civic Pass. */}
      <header className="public-bar">
        <a href="/" className="public-bar__logo"><img src="/brand/chicago-politics.png" alt="Chicago Politics" width={100} height={40} /></a>
        <span className="chip chip--neutral public-bar__chip"><Eye size={14} aria-hidden />Shared dashboard · view only</span>
        <span className="public-bar__meta">{stamp}{d.sharedBy ? ` · shared by ${d.sharedBy}` : ""}</span>
        <span className="spacer" />
        <a className="btn btn--secondary" href={`/signin?next=${linkNext}`}><LogIn size={15} aria-hidden />Sign in</a>
        <a className="btn btn--primary" href="/signup"><Ticket size={15} aria-hidden />Get a Civic Pass</a>
      </header>
      <main className="panel">
        <div className="ov-page an-dash">
          <header className="an-dash__head">
            <div className="an-dash__title">
              <div className="an-dash__h1"><h1 className="an-title">{d.title}</h1></div>
              {d.description && <p className="an-sub">{d.description}</p>}
            </div>
            <div className="an-dash__actions">
              <button className="btn btn--secondary" onClick={() => setPrinting(new Map(widgetViews))}><Printer size={15} aria-hidden />Print</button>
            </div>
          </header>
          <div className="an-filterbar an-filterbar--static" aria-label="Dashboard filters">
            <span className="eyebrow an-filterbar__label">Filters</span>
            {(["since", "topic", "wards"] as const).map((k) => <span key={k} className="an-filter__chip is-static">{filterChip(k, filters)}</span>)}
          </div>
          <MobileFilterChips filters={filters} />
          <CrossFilterBar />
          {printing ? null : phone ? <MobileGrid group={d.group} /> : <Grid group={d.group} fluid />}
          <ChartEditorHost />
          <p className="an-public__foot">Money near a vote isn’t evidence of influence.</p>
        </div>
        {printing && <PrintView dashboard={{ id: "link", title: d.title, layout: d.layout, print: d.print, link: d.link, filters: d.filters, version: d.version, updatedAt: d.updatedAt }}
          orgName={d.orgName} filters={filters} views={printing} link={`${location.host}/d/${d.link.token}`} onClose={() => setPrinting(null)} onSaveSetup={() => {}} />}
      </main>
    </div>
  );
}
